import { Box, Typography } from '@mui/material';
import { ReactNode } from 'react';

import { PieceAction } from '@apiContract';
import { DialogConfirmation } from '@uiKit/DialogConfirmation';

import { useFinishPieceMutation } from '../../hooks/useFinishPieceMutation';
import { useValidationExitGuard } from '../../hooks/useValidationExitGuard';
import { ActionComponentProps } from '../../index';
import NotifyMairoomComment from './NotifyComment';
import useNotifyComment from './NotifyComment/useNotifyComment';

type FinishActionType =
  | PieceAction.FinishDelivery
  | PieceAction.FinishPickUp
  | PieceAction.FinishRecycle
  | PieceAction.FinishReturnToSender
  | PieceAction.FinishShred;

type FinishActionConfig = {
  title: { single: string; plural: string };
  info: { single: ReactNode; plural: (count: number) => ReactNode };
};

const FINISH_ACTION_CONFIG: Record<FinishActionType, FinishActionConfig> = {
  [PieceAction.FinishDelivery]: {
    title: { single: 'Deliver item', plural: 'Deliver selected items' },
    info: {
      single: <>Please deliver this item to the recipient, and click Complete when you&apos;re done.</>,
      plural: (count) => (
        <>
          Please deliver <strong>{count}</strong> items to the recipient, and click Complete when you&apos;re done.
        </>
      ),
    },
  },
  [PieceAction.FinishPickUp]: {
    title: { single: 'Complete pickup', plural: 'Complete pickup for selected items' },
    info: {
      single: <>Please give this item to the recipient, and click Complete when you&apos;re done.</>,
      plural: (count) => (
        <>
          Please give <strong>{count}</strong> items to the recipient, and click Complete when you&apos;re done.
        </>
      ),
    },
  },
  [PieceAction.FinishRecycle]: {
    title: { single: 'Recycle item', plural: 'Recycle selected items' },
    info: {
      single: "Please recycle this item, and click Complete when you're done.",
      plural: (count) => (
        <>
          Please recycle <strong>{count}</strong> item(s), and click Complete when you&apos;re done.
        </>
      ),
    },
  },
  [PieceAction.FinishReturnToSender]: {
    title: { single: 'Return item to sender', plural: 'Return selected items to senders' },
    info: {
      single: <>Please return this item to its sender, and click Complete when you&apos;re done.</>,
      plural: (count) => (
        <>
          Please return <strong>{count}</strong> items to their senders, and click Complete when you&apos;re done.
        </>
      ),
    },
  },
  [PieceAction.FinishShred]: {
    title: { single: 'Shred item', plural: 'Shred selected items' },
    info: {
      single: "Please shred this item, and click Complete when you're done.",
      plural: (count) => (
        <>
          Please shred <strong>{count}</strong> item(s), and click Complete when you&apos;re done.
        </>
      ),
    },
  },
};

type FinishActionDialogProps = ActionComponentProps & {
  action: FinishActionType;
};

export const FinishActionDialog = ({
  mailpieces,
  actionStarted,
  onActionFinish,
  validationData,
  action,
}: FinishActionDialogProps) => {
  const { guardedExit, exitConfirmDialog } = useValidationExitGuard(validationData, onActionFinish);

  const { runAction, isLoading } = useFinishPieceMutation();

  const { values, changeValues } = useNotifyComment();

  const { title, info } = FINISH_ACTION_CONFIG[action];
  const isSingle = mailpieces.length === 1;

  const confirmAction = async () => {
    await runAction({
      action,
      pieces: mailpieces,
      comment: values.comment,
      notifyMailroom: values.notifyMailroom,
      skipNotifyRecipient: values.skipNotifyRecipient,
      validationData,
    });

    onActionFinish();
  };

  const content = (
    <Box>
      <Typography variant="body1" color="text.secondary">
        {isSingle ? info.single : info.plural(mailpieces.length)}
      </Typography>

      <NotifyMairoomComment values={values} onChange={changeValues} />
    </Box>
  );

  return (
    <>
      <DialogConfirmation
        open={actionStarted}
        loading={isLoading}
        maxWidth="sm"
        title={isSingle ? title.single : title.plural}
        content={content}
        acceptButtonHandleClick={confirmAction}
        cancelButtonHandleClick={guardedExit}
        acceptButtonTitle="Complete"
        cancelButtonTitle="Cancel"
      />

      {exitConfirmDialog}
    </>
  );
};